import {
  Box,
  Grid,
  Text,
  Button,
  Flex,
  Stack,
  VStack,
  useColorModeValue,
  SlideFade,
  Fade,
  useDisclosure,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {
  ApiCoverageInputParameters,
  ApiCheckParameters,
  ApiDownloadCoverageParameter,
} from "api/coverage/inputParameters";
import {
  apiCreateSimulation,
  apiReloadSimulation,
  type SimulationBootstrapPayload,
} from "api/simulation/createSimulation";
import { ApiLoadOrderList } from "api/coverage/buyOrderList";
import { ApiProductsAttributes } from "api/coverage/ProductAttributes";
import * as mdIcons from "react-icons/md";
import { InputParameter, InputPremise } from "./components/Premises";
import {
  filterPremisesForSimulatorDrawer,
  groupPremisesByOrigin,
} from "./components/premiseDisplayUtils";
import Card from "components/card/Card";
import AlertModal from "components/alertModal/AlertModal";
import { useCallback, useState, useEffect } from "react";
import { useAuth } from "contexts/AuthProvider";
import { useLoading } from "contexts/LoadingContext";
import { useNavigate } from "react-router-dom";

export type PremisesConfigurationVariant = "page" | "drawer";

export type PremisesConfigurationContentProps = {
  variant: PremisesConfigurationVariant;
  /** Si está definido, Cargar actualiza esta simulación en lugar de crear una nueva. */
  existingSimulationId?: string | null;
  /** En drawer: ejecutado al crear o recargar la simulación en el servidor. */
  onReadyToSimulate?: (payload?: SimulationBootstrapPayload) => void;
};

export function PremisesConfigurationContent({
  variant,
  existingSimulationId = null,
  onReadyToSimulate,
}: PremisesConfigurationContentProps) {
  const [premiseRows, setPremiseRows] = useState<InputPremise[] | null>(null);
  const [productOptions, setProductOptions] = useState<unknown[]>([]);
  const [isReload, setReload] = useState(0);
  const [error, setError] = useState<{
    type: string;
    message: string | JSX.Element;
    title?: string;
  } | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const nav = useNavigate();
  const { logoutAction, userActions } = useAuth();
  const { show, hide } = useLoading();

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "gray.400");
  const sectionBg = useColorModeValue("secondaryGray.50", "whiteAlpha.50");
  const footerBorderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
  const progressColorPrimary = useColorModeValue("blue", "#20b320");
  const premiseLayout = variant === "drawer" ? "list" : "card";

  const proceedToSimulation = (payload?: SimulationBootstrapPayload) => {
    userActions.simulating.change(true);
    if (variant === "drawer" && onReadyToSimulate) {
      onReadyToSimulate(payload);
    } else if (payload) {
      nav("/coverage/simulator", { state: { simulationBootstrap: payload } });
    } else {
      nav("/coverage/simulator");
    }
  };

  const applyCreateSimulationError = useCallback((err: unknown) => {
    const ax = err as { response?: { data?: unknown } };
    let message =
      "No se pudo crear la simulación. Revisá parámetros y sesión.";
    if (err instanceof Error && err.message) {
      message = err.message;
    }
    const data = ax.response?.data as Record<string, unknown> | undefined;
    if (data?.errors) {
      message =
        typeof data.errors === "string"
          ? data.errors
          : JSON.stringify(data.errors);
    }
    if (typeof (data as { detail?: unknown } | undefined)?.detail === "string") {
      message = String((data as { detail: string }).detail);
    }
    setError({
      type: "error",
      message,
      title: "¡Aún no estamos listos!",
    });
    onOpen();
  }, [onOpen]);

  const fetchPremiseRows = useCallback(async () => {
    const premiseRes = await ApiCoverageInputParameters();
    return (premiseRes.data ?? []) as Array<{
      name: string;
      value: unknown;
    }>;
  }, []);

  /** POST /api/v1/simulations/ y navegación / callback con el payload. */
  const createSimulationAndProceed = useCallback(async () => {
    const rows = await fetchPremiseRows();
    const payload = await apiCreateSimulation({
      premiseRows: rows,
    });
    proceedToSimulation(payload);
  }, [fetchPremiseRows, proceedToSimulation]);

  /** POST /api/v1/simulations/{id}/reload/ — misma simulación, premisas nuevas. */
  const reloadSimulationAndProceed = useCallback(async () => {
    const simId = (existingSimulationId ?? "").trim();
    if (!simId) {
      await createSimulationAndProceed();
      return;
    }
    const rows = await fetchPremiseRows();
    const payload = await apiReloadSimulation(simId, { premiseRows: rows });
    proceedToSimulation(payload);
  }, [
    createSimulationAndProceed,
    existingSimulationId,
    fetchPremiseRows,
    proceedToSimulation,
  ]);

  const startSimulationFromPremises = useCallback(async () => {
    if (existingSimulationId?.trim()) {
      await reloadSimulationAndProceed();
      return;
    }
    await createSimulationAndProceed();
  }, [
    createSimulationAndProceed,
    existingSimulationId,
    reloadSimulationAndProceed,
  ]);

  const getPremises = async () => {
    show();
    await ApiCoverageInputParameters()
      .then(async function (response) {
        const data: Array<Record<string, unknown>> = response.data;
        const products = await ApiProductsAttributes().then(
          (response) => response.data
        );
        setProductOptions(products);
        const rows = data.map((premise) => ({
          id: premise.id as number,
          name: premise.name as string,
          description: premise.description as string,
          origin: premise.origin as string,
          lastUpdate: premise.last_update as Date | null,
          lastModifiedBy: premise.last_modified_by as string,
          value: premise.value,
          downloadable: premise.downloadable as boolean,
          itemCount: premise.item_count as bigint | null,
          displayIcon: premise.display_icon as string,
          summarizingString: premise.summarizing_string as string,
          isConfigurable: premise.is_configurable as boolean,
          type: premise.type as string,
        }));
        setPremiseRows(
          variant === "drawer" ? filterPremisesForSimulatorDrawer(rows) : rows,
        );
        hide();
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          logoutAction(true);
        } else {
          console.log(err);
        }
        hide();
      });
  };

  const loadOrders = async () => {
    show();
    await ApiLoadOrderList()
      .then(() => {
        hide();
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          logoutAction(true);
        } else {
          setError({
            type: "error",
            message:
              "Ocurrió un error cargando las ordenes de compra, intente de nuevo.",
          });
        }
        hide();
      });
  };

  const runFullLoad = async () => {
    setError(null);
    try {
      await loadOrders();
      show();
      const checkRes = await ApiCheckParameters();
      const _res: { warning?: string | null } = checkRes.data;
      if (_res.warning != null && String(_res.warning).trim() !== "") {
        setError({
          type: "warning",
          message: _res.warning,
          title: "¡Advertencia!",
        });
        onOpen();
        return;
      }

      await startSimulationFromPremises();
    } catch (err: unknown) {
      applyCreateSimulationError(err);
    } finally {
      hide();
    }
  };

  const handleParameterDownload = async () => {
    show();
    ApiDownloadCoverageParameter(3, "forecast_missing_products").then(
      (response) => {
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        const d = new Date();
        a.download = `Productos sin Forecast (${d.toLocaleString()}).xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        hide();
      }
    );
  };

  useEffect(() => {
    if (variant === "page" && userActions.simulating.value) {
      nav("/coverage/simulator");
    }
    getPremises();
  }, [isReload]);

  const footerButtonLabel = variant === "drawer" ? "Cargar" : "Continuemos...";
  const drawerContinuePrompt =
    variant === "drawer" ? "¿Listo para cargar?" : "¿Todo listo?";
  const isUpdatingExisting = Boolean(existingSimulationId?.trim());
  const loadActionDescription = isUpdatingExisting
    ? "se sincronizan órdenes de compra y se actualiza la simulación activa en el servidor."
    : "se sincronizan órdenes de compra y se crea la simulación en el servidor.";

  const gridColumns =
    variant === "drawer"
      ? { base: "1fr" }
      : {
          base: "1fr",
          md: "1fr 1fr 1fr",
          lg: "1fr 1fr 1fr 1fr",
          xl: "1fr 1fr 1fr 1fr 1fr",
          "2xl": "1fr 1fr 1fr 1fr 1fr 1fr",
        };

  const gridFooter =
    variant === "drawer" ? (
      <Flex direction="column" w="100%" gap={3}>
        <Text color={textColorSecondary} fontSize="sm" lineHeight="tall">
          {drawerContinuePrompt} Al pulsar{" "}
          <Text as="span" fontWeight="600" color={textColorPrimary}>
            {footerButtonLabel}
          </Text>{" "}
          {loadActionDescription}
        </Text>
        <Button
          w="100%"
          size="md"
          fontSize="sm"
          backgroundColor={progressColorPrimary}
          color="white"
          onClick={() => runFullLoad()}
        >
          {footerButtonLabel}
        </Button>
      </Flex>
    ) : (
      <SlideFade in={true} delay={2} offsetX="-50px" offsetY="0px">
        <Card h="fit-content" w={{ md: "fit-content" }} py="15px" px="25px">
          <Flex w="100%" alignItems="center">
            <Text
              color={textColorPrimary}
              fontSize={{ base: "15px", lg: "1x1" }}
              alignSelf="center"
              w={{ base: "fit-content", md: "auto" }}
            >
              {drawerContinuePrompt}
            </Text>
            <Button
              ml={{ base: "10px" }}
              fontSize={{ base: "15px" }}
              backgroundColor={progressColorPrimary}
              color="white"
              onClick={() => runFullLoad()}
            >
              {footerButtonLabel}
            </Button>
          </Flex>
        </Card>
      </SlideFade>
    );

  const renderPremise = (premise: InputPremise, index: number) => (
    <InputParameter
      key={premise.id}
      premise={premise}
      index={index}
      reloader={setReload}
      layout={premiseLayout}
      productOptions={productOptions}
    />
  );

  const premisesDrawerList =
    premiseRows == null ? null : (
      <Stack spacing={6} pb={2}>
        {groupPremisesByOrigin(premiseRows).map(({ group, items }) => (
          <Box key={group.key}>
            <Box
              px={3}
              py={2}
              mb={3}
              borderRadius="12px"
              bg={sectionBg}
            >
              <Text
                color={textColorPrimary}
                fontSize="sm"
                fontWeight="700"
                letterSpacing="0.02em"
              >
                {group.title}
              </Text>
              <Text color={textColorSecondary} fontSize="xs" mt={0.5}>
                {group.hint}
              </Text>
            </Box>
            <VStack spacing={3} align="stretch">
              {items.map((premise, idx) => renderPremise(premise, idx))}
            </VStack>
          </Box>
        ))}
      </Stack>
    );

  const premisesGrid =
    premiseRows == null ? null : (
      <Grid
        templateColumns={gridColumns}
        templateRows={
          variant === "drawer"
            ? undefined
            : {
                base: "repeat(3, 1fr)",
                lg: "1fr",
              }
        }
        gap="20px"
        overflowY={variant === "drawer" ? "visible" : { base: "scroll", md: "hidden" }}
        pb={variant === "drawer" ? "24px" : "150px"}
      >
        {premiseRows.map((premise, idx) => renderPremise(premise, idx))}
      </Grid>
    );

  return (
    <>
      <AlertModal
        status={error?.type}
        message={error?.message}
        title={error?.title}
        disclosure={{ isOpen: isOpen, onOpen: onOpen, onClose: onClose }}
        iconProps={{ w: "70px", h: "75px" }}
        buttons={
          <>
            {error?.type === "error" ? null : (
              <Button
                onClick={handleParameterDownload}
                color={textColorPrimary}
                mr="2%"
              >
                <Icon as={mdIcons.MdDownload} h="16px" w="16px" />
              </Button>
            )}
            <Button
              onClick={async () => {
                const modalError = error;
                onClose();
                if (!modalError || modalError.type === "error") {
                  return;
                }
                if (modalError.type === "warning") {
                  try {
                    show();
                    await startSimulationFromPremises();
                  } catch (err: unknown) {
                    applyCreateSimulationError(err);
                  } finally {
                    hide();
                  }
                  return;
                }
                proceedToSimulation(undefined);
              }}
              color={textColorPrimary}
            >
              <Text fontSize="sm">Entendido</Text>
            </Button>
          </>
        }
      />
      {variant === "drawer" ? (
        <Flex direction="column" w="100%">
          <Box mb={5} flexShrink={0}>
            <Text
              color={textColorPrimary}
              fontSize="md"
              fontWeight="700"
            >
              Información del ERP y fuentes de datos
            </Text>
            <Text color={textColorSecondary} fontSize="sm" mt={1} lineHeight="tall">
              Revisá el estado de cada fuente antes de cargar la simulación. Los
              parámetros pendientes pueden impedir el cálculo de cobertura.
            </Text>
            <Divider my={4} />
          </Box>
          {premisesDrawerList ?? premisesGrid}
          <Box
            mt={6}
            flexShrink={0}
            pb={1}
            pt={4}
            borderTopWidth="1px"
            borderColor={footerBorderColor}
          >
            {gridFooter}
          </Box>
        </Flex>
      ) : (
        <>
          {premisesGrid}
          <Flex
            w={{ base: "84%", md: "90%", lg: "92%", xl: "93%", "2xl": "95%" }}
            justifyContent={{ md: "space-between", base: "center" }}
            position="absolute"
            bottom="20px"
          >
            <Fade in={true} delay={1.5}>
              <Text
                color={textColorPrimary}
                fontSize="1x1"
                alignContent="center"
                fontWeight="500"
                height="100%"
                display={{ base: "none", md: "block" }}
                pr="10vw"
              >
                Estas son las distintas opciones y fuentes de datos que se tomarán
                en cuenta para calcular los días de cobertura de cada uno de los
                productos.
              </Text>
            </Fade>
            {gridFooter}
          </Flex>
        </>
      )}
    </>
  );
}
